import { useCallback, useMemo, useState } from 'react'
import {
  ColorOverride,
  FieldDefinition,
  FieldSpan,
  HighlightRule,
  HighlightTone,
  LayoutSettings,
  PaletteColor,
  PanelField,
  ToneColors,
} from '../types/highlights'
import { defaultPanelFields, fieldCatalog } from '../data/fieldCatalog'
import { defaultRules } from '../data/defaultRules'
import { defaultToneColors } from '../utils/palette'
import { defaultLayout } from '../utils/layout'
import { createRuleId, defaultOperatorFor } from '../utils/rules'

export function useHighlightsPanel() {
  const [fields, setFields] = useState<PanelField[]>(defaultPanelFields)
  const [toneColors, setToneColors] = useState<ToneColors>(defaultToneColors)
  const [rules, setRules] = useState<HighlightRule[]>(defaultRules)
  const [layout, setLayoutState] = useState<LayoutSettings>(defaultLayout)

  const byId = useMemo(() => {
    const map = new Map<string, FieldDefinition>()
    fieldCatalog.forEach((definition) => map.set(definition.id, definition))
    return map
  }, [])

  const availableFields = useMemo(
    () => fieldCatalog.filter((definition) => !fields.some((f) => f.fieldId === definition.id)),
    [fields],
  )

  const addField = useCallback((fieldId: string) => {
    setFields((current) =>
      current.some((f) => f.fieldId === fieldId) ? current : [...current, { fieldId }],
    )
  }, [])

  const removeField = useCallback((fieldId: string) => {
    setFields((current) => current.filter((f) => f.fieldId !== fieldId))
  }, [])

  const setColorOverride = useCallback((fieldId: string, colorOverride?: ColorOverride) => {
    setFields((current) =>
      current.map((f) => (f.fieldId === fieldId ? { ...f, colorOverride } : f)),
    )
  }, [])

  const clearAllOverrides = useCallback(() => {
    setFields((current) =>
      current.map(({ fieldId, span, maxChars }) => ({ fieldId, span, maxChars })),
    )
  }, [])

  const moveField = useCallback((from: number, to: number) => {
    setFields((current) => {
      if (from === to || to < 0 || to >= current.length) return current
      const next = [...current]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }, [])

  const setFieldSpan = useCallback((fieldId: string, span: FieldSpan) => {
    setFields((current) => current.map((f) => (f.fieldId === fieldId ? { ...f, span } : f)))
  }, [])

  const setFieldMaxChars = useCallback((fieldId: string, maxChars?: number) => {
    setFields((current) =>
      current.map((f) => (f.fieldId === fieldId ? { ...f, maxChars } : f)),
    )
  }, [])

  const setLayout = useCallback((patch: Partial<LayoutSettings>) => {
    setLayoutState((current) => ({ ...current, ...patch }))
  }, [])

  const normalizeFieldSizes = useCallback(() => {
    setFields((current) =>
      current.map(({ fieldId, colorOverride }) => ({ fieldId, colorOverride })),
    )
  }, [])

  const setToneColor = useCallback((tone: HighlightTone, color: PaletteColor) => {
    setToneColors((current) => ({ ...current, [tone]: color }))
  }, [])

  const addRule = useCallback(
    (targetFieldId: string) => {
      const definition = byId.get(targetFieldId)
      const rule: HighlightRule = {
        id: createRuleId(),
        targetFieldId,
        sourceFieldId: targetFieldId,
        operator: definition ? defaultOperatorFor(definition.type) : 'eq',
        value: '',
        tone: 'warning',
        enabled: true,
      }
      setRules((current) => [...current, rule])
      return rule.id
    },
    [byId],
  )

  const updateRule = useCallback((ruleId: string, patch: Partial<HighlightRule>) => {
    setRules((current) =>
      current.map((rule) => (rule.id === ruleId ? { ...rule, ...patch } : rule)),
    )
  }, [])

  const removeRule = useCallback((ruleId: string) => {
    setRules((current) => current.filter((rule) => rule.id !== ruleId))
  }, [])

  const resetFields = useCallback(() => setFields(defaultPanelFields), [])

  const resetHighlightSettings = useCallback(() => {
    setToneColors(defaultToneColors)
    setRules(defaultRules)
    setLayoutState(defaultLayout)
  }, [])

  return {
    fields,
    byId,
    availableFields,
    toneColors,
    rules,
    layout,
    addField,
    removeField,
    setColorOverride,
    clearAllOverrides,
    moveField,
    setFieldSpan,
    setFieldMaxChars,
    setLayout,
    normalizeFieldSizes,
    setToneColor,
    addRule,
    updateRule,
    removeRule,
    resetFields,
    resetHighlightSettings,
  }
}
